import { openDB, type IDBPDatabase } from 'idb'
import moment from 'moment'

const DB_NAME = 'inspect-events'
const DB_VERSION = 1
const EVENTS_STORE = 'calendarEvents'

let dbInstance: IDBPDatabase | null = null

/**
 * Initialize IndexedDB for calendar events storage
 */
async function getDB(): Promise<IDBPDatabase> {
  if (dbInstance) return dbInstance

  dbInstance = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create calendar events store with dateKey as primary key
      if (!db.objectStoreNames.contains(EVENTS_STORE)) {
        const eventStore = db.createObjectStore(EVENTS_STORE, { keyPath: 'dateKey' })
        eventStore.createIndex('lastModified', 'lastModified')
      }
    }
  })

  return dbInstance
}

/**
 * Save calendar events for a specific date to IndexedDB
 * @param dateKey - Date in format YYYY-MM-DD
 * @param events - Array of calendar events
 */
export async function setEvent(dateKey: string, events: CalendarEvent[]): Promise<void> {
  try {
    const db = await getDB()

    // Convert Vue reactive objects to plain objects for IndexedDB
    // IndexedDB cannot store Proxies (Vue's reactivity system)
    const plainEvents = JSON.parse(JSON.stringify(events))

    await db.put(EVENTS_STORE, {
      dateKey,
      events: plainEvents,
      lastModified: Date.now()
    })
    console.log('[EventStorage] Saved events for date:', dateKey, plainEvents.length)
  } catch (error) {
    console.error('[EventStorage] Failed to save events:', error)
    throw error
  }
}

/**
 * Get calendar events for a specific date from IndexedDB
 * @param dateKey - Date in format YYYY-MM-DD
 * @returns Array of calendar events or null if not found
 */
export async function getEvent(dateKey: string): Promise<CalendarEvent[] | null> {
  try {
    const db = await getDB()
    const result = await db.get(EVENTS_STORE, dateKey)

    if (result?.events) {
      console.log(
        '[EventStorage] Loaded events from cache for date:',
        dateKey,
        result.events.length
      )
      return result.events
    }

    return null
  } catch (error) {
    console.error('[EventStorage] Failed to get events:', error)
    return null
  }
}

/**
 * Update a single event in storage
 * @param eventId - Calendar event ID
 * @param dateKey - Date key where event is stored
 * @param updates - Partial event data to merge
 */
export async function updateEventInStorage(
  eventId: number,
  dateKey: string,
  updates: Partial<CalendarEvent>
): Promise<void> {
  try {
    const events = await getEvent(dateKey)
    if (!events) {
      console.warn('[EventStorage] No events found for date:', dateKey)
      return
    }

    const eventIndex = events.findIndex((e) => e.id === eventId)
    if (eventIndex === -1) {
      console.warn('[EventStorage] Event not found:', eventId)
      return
    }

    // Convert updates to plain object to avoid Vue reactivity issues
    const plainUpdates = JSON.parse(JSON.stringify(updates))

    // Merge updates with existing event
    events[eventIndex] = {
      ...events[eventIndex],
      ...plainUpdates
    }

    await setEvent(dateKey, events)
    console.log('[EventStorage] Updated event:', eventId)
  } catch (error) {
    console.error('[EventStorage] Failed to update event:', error)
    throw error
  }
}

/**
 * Get a single event by ID from storage
 * @param eventId - Calendar event ID
 * @param dateKey - Date key where event might be stored
 * @returns Calendar event or null if not found
 */
export async function getEventById(
  eventId: number,
  dateKey: string
): Promise<CalendarEvent | null> {
  try {
    const events = await getEvent(dateKey)
    if (!events) return null

    return events.find((e) => e.id === eventId) || null
  } catch (error) {
    console.error('[EventStorage] Failed to get event by ID:', error)
    return null
  }
}

/**
 * Clear events older than specified days
 * @param daysToKeep - Number of days to keep (default: 30)
 */
export async function clearOldEvents(daysToKeep = 30): Promise<void> {
  try {
    const db = await getDB()
    const tx = db.transaction(EVENTS_STORE, 'readwrite')
    const store = tx.objectStore(EVENTS_STORE)
    const index = store.index('lastModified')

    const cutoffTime = moment().subtract(daysToKeep, 'days').valueOf()

    let cursor = await index.openCursor(IDBKeyRange.upperBound(cutoffTime))

    while (cursor) {
      await cursor.delete()
      cursor = await cursor.continue()
    }

    await tx.done
    console.log('[EventStorage] Cleaned up old events')
  } catch (error) {
    console.error('[EventStorage] Failed to clear old events:', error)
  }
}

/**
 * Get all stored date keys
 * @returns Array of date keys
 */
export async function getAllDateKeys(): Promise<string[]> {
  try {
    const db = await getDB()
    return (await db.getAllKeys(EVENTS_STORE)) as string[]
  } catch (error) {
    console.error('[EventStorage] Failed to get all date keys:', error)
    return []
  }
}

/**
 * Clear all events from storage
 */
export async function clearAllEvents(): Promise<void> {
  try {
    const db = await getDB()
    await db.clear(EVENTS_STORE)
    console.log('[EventStorage] Cleared all events')
  } catch (error) {
    console.error('[EventStorage] Failed to clear all events:', error)
    throw error
  }
}

/**
 * Get storage statistics
 */
export async function getStorageStats(): Promise<{
  totalDates: number
  totalEvents: number
  oldestDate: string | null
  newestDate: string | null
}> {
  try {
    const db = await getDB()
    const allKeys = await db.getAllKeys(EVENTS_STORE)
    const allData = await db.getAll(EVENTS_STORE)

    const totalEvents = allData.reduce((sum, item) => sum + (item.events?.length || 0), 0)
    const sortedKeys = (allKeys as string[]).sort()

    return {
      totalDates: allKeys.length,
      totalEvents,
      oldestDate: sortedKeys[0] || null,
      newestDate: sortedKeys[sortedKeys.length - 1] || null
    }
  } catch (error) {
    console.error('[EventStorage] Failed to get storage stats:', error)
    return {
      totalDates: 0,
      totalEvents: 0,
      oldestDate: null,
      newestDate: null
    }
  }
}
